// src/components/FinlandRegionMap/FinlandRegionMap.jsx
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import FinlandMap from "../../assets/finlandmap-maakunnilla.webp";

// Palloille suunnilleen oikeat sijainnit kartalla
const REGION_BUBBLES = [
  { regionName: "Lappi",            label: "Lappi",           top: "25%", left: "56%" },
  { regionName: "Kainuu",           label: "Kainuu",          top: "52%", left: "72%" },
  { regionName: "Pohjois-Pohjanmaa",label: "Pohj.-Pohj.",     top: "50%", left: "56%" },
  { regionName: "Keski-Pohjanmaa",  label: "Keski-Pohj.",     top: "63%", left: "42%" },
  { regionName: "Pohjanmaa",        label: "Pohjanmaa",       top: "67%", left: "27%" },
  { regionName: "Etelä-Pohjanmaa",  label: "Etelä-Pohj.",     top: "70%", left: "35%" },
  { regionName: "Keski-Suomi",      label: "Keski-Suomi",     top: "71%", left: "50%" },
  { regionName: "Pohjois-Savo",     label: "Pohj.-Savo",      top: "66%", left: "65%" },
  { regionName: "Pohjois-Karjala",  label: "Pohj.-Karjala",   top: "70%", left: "85%" },
  { regionName: "Etelä-Savo",       label: "Etelä-Savo",      top: "78%", left: "70%" },
  { regionName: "Etelä-Karjala",    label: "Etelä-Karjala",   top: "86%", left: "70%" },
  { regionName: "Kymenlaakso",      label: "Kymenlaakso",     top: "88%", left: "60%" },
  { regionName: "Päijät-Häme",      label: "Päijät-Häme",     top: "85%", left: "53%" },
  { regionName: "Pirkanmaa",        label: "Pirkanmaa",       top: "80%", left: "38%" },
  { regionName: "Satakunta",        label: "Satakunta",       top: "84%", left: "26%" },
  { regionName: "Varsinais-Suomi",  label: "Varsinais-Suomi", top: "90%", left: "30%" },
  { regionName: "Kanta-Häme",       label: "Kanta-Häme",      top: "88%", left: "43%" },
  { regionName: "Uusimaa",          label: "Uusimaa",         top: "92%", left: "48%" },
  { regionName: "Ahvenanmaa",       label: "Ahvenanmaa",      top: "93%", left: "8%" },
];

function FinlandRegionMap({
  regions,
  selectedRegionId,
  regionsWithPrices,
  onSelectRegion,
  regionPriceMap, // 🔹 { [regionId]: { [setLabel]: priceString } }
}) {
  // --- Hooks aina ensin ---

  // Muodostetaan nimi → regionObj -map, jotta palloista on helppo löytää backendin maakuntaobjekti
  const regionsByName = useMemo(() => {
    const m = new Map();
    (regions || []).forEach((r) => {
      if (r?.name) m.set(r.name, r);
    });
    return m;
  }, [regions]);

  // id:t niille maakunnille, joilla on vähintään yksi hinta
  const pricedRegionIds = useMemo(
    () => new Set((regionsWithPrices || []).map((r) => r.id)),
    [regionsWithPrices]
  );

  const hasAnyRegions = Array.isArray(regions) && regions.length > 0;

  // 🔹 Aktiivinen maakunta-objekti isoa labelia varten
  const selectedRegion =
    hasAnyRegions && selectedRegionId != null
      ? regions.find((r) => r.id === selectedRegionId) || null
      : null;

  // Jos ei ole lainkaan maakuntadataa, ei piirretä karttaa
  if (!hasAnyRegions) {
    return null;
  }

  // Tarkistaa, onko tietyllä maakunnalla hintoja
  const hasPricesForRegion = (regionName) => {
    const regionObj = regionsByName.get(regionName);
    if (!regionObj) return false;
    return pricedRegionIds.has(regionObj.id);
  };

  // 🔹 Palauttaa hinnoitellut settilabelit yhdelle maakunnalle (0 € lasketaan mukaan, tyhjä = piilossa)
  const getSetLabelsForRegion = (regionId) => {
    if (!regionPriceMap) return [];
    const raw = regionPriceMap[regionId];
    if (!raw || typeof raw !== "object") return [];

    const labels = [];
    for (const [label, rawPrice] of Object.entries(raw)) {
      const s = String(rawPrice ?? "").trim();
      if (!s) continue; // tyhjä kenttä = ei hintaa → ei näytetä
      labels.push(label);
    }

    return labels.sort((a, b) => a.localeCompare(b, "fi"));
  };

  // Klikkaus kartasta → nostetaan valinta ylös parent-komponentille
  const handleClickRegion = (regionObj) => {
    if (!regionObj) return;
    if (typeof onSelectRegion === "function") {
      onSelectRegion(regionObj);
    }
  };

  return (
    <div className="mt-2">
      <div className="mx-4 mb-3 rounded-xl bg-white/70 dark:bg-black/40 px-3 py-2 ring-1 ring-black/10 dark:ring-white/10">
        <p className="font-customFont1 text-[11px] md:text-sm text-black/80 dark:text-white/80 leading-snug">
          <span className="font-semibold">Klikkaa kartasta maakuntaa.</span>
          <br />
          <span className="font-semibold text-emerald-400">Vihreä pallo</span> = Hintoja alueella
          <br />{" "}
          <span className="font-semibold text-red-400">Punainen pallo</span> = ei vielä hintaa{" "}
        </p>
      </div>

      {/* Varsinainen karttakehys */}
      <div
        className="
          relative mx-auto w-full max-w-xs md:max-w-sm
          aspect-[3/5] rounded-2xl overflow-hidden
        "
      >
        {/* Suomen ääriviivat taustalla yhtenä kuvana */}
        <div
          className="absolute inset-0 dark:opacity-40 opacity-90"
          style={{
            backgroundImage: `url(${FinlandMap})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}
        />

        {/* 🔹 Valitun maakunnan nimi kartan alareunassa isommalla */}
        {selectedRegion && (
          <div className="pointer-events-none absolute inset-x-0 bottom-1 flex justify-center z-10">
            <div
              className="
                rounded-full bg-black/70 dark:bg-black/80
                px-2.5 py-0.5
                text-lg font-semibold tracking-wide
                text-white drop-shadow-lg
              "
            >
              {selectedRegion.name}
            </div>
          </div>
        )}

        {/* Maakuntapallot kartan päälle absolute-positionilla */}
        {REGION_BUBBLES.map((b) => {
          const regionObj = regionsByName.get(b.regionName);
          if (!regionObj) return null;

          const isSelected =
            selectedRegionId != null && regionObj.id === selectedRegionId;
          const hasPrices = hasPricesForRegion(b.regionName);

          // Perusväri (ei valittu)
          const baseBgClass = hasPrices
            ? "bg-lime-400 drop-shadow-lg ring ring-offset-1 opacity-90"
            : "bg-red-600 drop-shadow-lg";

          // Aktiivisen pallon väri
          // ⬅️ MUUTA TÄTÄ jos haluat eri värin aktiiviselle
          const activeBgClass = isSelected
            ? "bg-primary ring-1 ring-white/100 ring-offset-2 drop-shadow-[0_0_10px_rgba(52,211,153,0.9)]"
            : baseBgClass;

          const textClass = "text-transparent";

          // 🔹 Tooltip: maakunnan nimi + siinä hinnoitellut setit
          const setLabels = regionObj ? getSetLabelsForRegion(regionObj.id) : [];
          const tooltipTitle = setLabels.length
            ? `${regionObj.name}: ${setLabels.join(", ")}`
            : regionObj?.name ?? b.regionName;

          return (
            <button
              key={b.regionName}
              type="button"
              aria-label={b.label}
              title={tooltipTitle} // 🔹 hoverissa näkyvä teksti
              className={[
                "group",
                "absolute flex items-center justify-center cursor-pointer",
                "rounded-full text-[9px] md:text-[11px] font-medium",
                "shadow-md transition-transform hover:scale-115",
              ].join(" ")}
              style={{
                top: b.top,
                left: b.left,
                transform: "translate(-50%, -50%)",
                width: "16px",
                height: "16px",
              }}
              onClick={() => handleClickRegion(regionObj)}
            >
              {/* Ulompi bile-halo vain valitulle maakunnalle */}
              {isSelected && (
                <span
                  aria-hidden="true"
                  className="
                    absolute inline-flex h-7 w-7 rounded-full
                    bg-pink-500/100
                    animate-bile-ping
                  "
                />
              )}

              {/* Varsinainen pallo + hover-ringit */}
              <span
                className={[
                  "relative inline-flex rounded-full",
                  "w-4 h-4",
                  activeBgClass,
                  textClass,
                  "transition-all duration-200",
                  !isSelected
                    ? "group-hover:ring-2 group-hover:ring-white group-hover:shadow-lg"
                    : "",
                  isSelected
                    ? "animate-pulse ring-2 ring-pink-800 shadow-lg"
                    : "",
                ].join(" ")}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

FinlandRegionMap.propTypes = {
  regions: PropTypes.array.isRequired,
  selectedRegionId: PropTypes.number,
  regionsWithPrices: PropTypes.array,
  onSelectRegion: PropTypes.func,
  // 🔹 price data kartalle: { [regionId]: { [setLabel]: priceString } }
  regionPriceMap: PropTypes.object,
};

export default FinlandRegionMap;
